export interface ILoggingParams {
    level: string,
    category: string,
    message?: string,
    exception?: any,
    optionalParams?: any[]
}

export interface ILoggerAdapter {
    log : (params: ILoggingParams) => void;
}

export class Logger implements ILoggerAdapter {
    static isEnabled = true;
    static categories: any = {};
    static default = new Logger(null);
    category: string;

    constructor(category: any) {
        // @ifndef DEBUG
        // Disable logging if not debug
        Logger.isEnabled = false;
        // @endif 

        this.category = category || "";
        if (this.category.length)
            this.category += ":";
    }

    info(category: string, message?: string, ...optionalParams: any[]): void {
        Logger.info.apply(this, ([`${this.category}${category || ""}`, message] as any).concat(optionalParams));
    }

    warn(category: string, message?: string, ...optionalParams: any[]): void {
        Logger.warn.apply(this, ([`${this.category}${category || ""}`, message] as any).concat(optionalParams));
    }

    error(category: string, message?: string, exception?: any, ...optionalParams: any[]): void {
        Logger.error.apply(this, ([`${this.category}${category || ""}`, message, exception] as any).concat(optionalParams));
    }

    debug(category: string, message?: string, ...optionalParams: any[]): void {
        Logger.debug.apply(this, ([`${this.category}${category || ""}`, message] as any).concat(optionalParams));
    }

    trace(category: string, message?: string, ...optionalParams: any[]): void {
        Logger.trace.apply(this, ([`${this.category}${category || ""}`, message] as any).concat(optionalParams));
    }

    log(logParams: ILoggingParams) {
        if (!Logger.isEnabled)
            return;

        var level = logParams.level;
        var category = logParams.category;
        var message = logParams.message;
        var optionalParams = logParams.optionalParams ?? [];

        if (!Logger.isEnabled)
            return;
        var msg = `${category}\t${message || ""}`;
        if (optionalParams.length === 1 && Array.isArray(optionalParams[0]) && (optionalParams[0]).length === 0)
            optionalParams = [];

        var logFunc: any;

        switch(level) {
            case "debug":
                logFunc = console.debug;
                break;
            case "error":
                logFunc = console.error;
                break;
            case "trace":
                logFunc = console.trace;
                break;
            case "warn":
                logFunc = console.warn;
                break;
            default:
                logFunc = console.log;
        }

        if (optionalParams.length)
            logFunc.call(console, msg, optionalParams);
        else
            logFunc.call(console, msg);
    }

    static getLog(category: string): Logger {
        if (!category || !category.trim().length)
            return Logger.default;

        var log = Logger.categories[category];
        if (!log) {
            log = new Logger(category);
            Logger.categories[category] = log;
        }
        return log;
    }

    static info(category: string, message?: string, ...optionalParams: any[]): void {
        Logger.log.apply(this, (["info", category, message, null] as any).concat(optionalParams));
    }

    static warn(category: string, message?: string, ...optionalParams: any[]): void {
        Logger.log.apply(this, (["warn", category, message, null] as any).concat(optionalParams));
    }

    static error(category: string, message?: string, exception?: any, ...optionalParams: any[]): void {
        Logger.log.apply(this, (["error", category, message, exception] as any).concat(optionalParams));
    }

    static debug(category: string, message?: string, ...optionalParams: any[]): void {
        Logger.log.apply(this, (["debug", category, message, null] as any).concat(optionalParams));
    }

    static trace(category: string, message?: string, ...optionalParams: any[]): void {
        Logger.log.apply(this, (["trace", category, message, null] as any).concat(optionalParams));
    }

    private static log(level: string, category: string, message?: string, exception?: any, ...optionalParams: any[]): void {
        Logger.default.log({ 
            level: level, 
            category: category, 
            message: message, 
            exception: exception, 
            optionalParams: optionalParams
        });
    }
}